<!--
  Sidebar panel for the info-modal visual builder. Shows the editor for the block selected on the
  canvas: its inline media upload (logo / step image), the shared catalog-driven BuilderInspector,
  and — for Step 2 — the checklist editor (an array, which the generic BuilderControl can't render).
  Every edit writes through to the flat `infoModalFormValues` the canvas + saved config render from.
-->
<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm font-semibold text-typography-primary">Properties</p>

    <div v-if="!selectedNode" class="text-sm text-typography-disabled py-6 text-center">
      Select an element in the preview to edit it.
    </div>

    <template v-else>
      <!-- Inline image upload for the selected block (logo / step image). -->
      <div v-for="mediaField in blockMedia" :key="mediaField.field" class="media-uploader-sm flex flex-col gap-2">
        <p class="text-2xs font-semibold uppercase tracking-wide text-typography-disabled">{{ mediaField.label }}</p>
        <MediaUploader
          :images="[fieldValue(mediaField.field) || '']"
          accept="image/*"
          label="Image"
          @update="files => handleMediaUpdate(mediaField.field, files)"
        />
      </div>

      <!-- Catalog-driven controls (text / number / textarea / toggle). -->
      <BuilderInspector :node="selectedNode" :catalog="INFO_MODAL_CATALOG" @change="onChange" />

      <!-- Step 2: checklist editor (array — no generic control kind). -->
      <div v-if="selectedNode.type === InfoModalBlockType.STEP_TWO" class="flex flex-col gap-2">
        <label class="text-2xs font-bold text-typography-disabled">Checklist Items</label>
        <ComparisonCartFeatures v-model="infoModalFormValues.modalChecklistItems" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCustomizationsStore } from '~/store/customizations';
import { useMediaStore } from '~/store/media';
import { useToastStore } from '~/store/toast';
import BuilderInspector from '~/components/builder/BuilderInspector.vue';
import ComparisonCartFeatures from '~/components/ComparisonCartFeatures.vue';
import MediaUploader from '~/components/shared/image-uploader/MediaUploader.vue';
import { INFO_MODAL_CATALOG, InfoModalBlockType } from '~/builder/catalog';
import { findNode } from '~/builder/useBuilderTree';
import { syncBlockChangeToInfoModal } from '~/builder/useInfoModalSeed';
import { CLAIM_MEDIA_TYPES } from '~/support/constants';
import type { InfoModalFormValues } from '~/types/widget';

type ImageField = 'modalLogoImage' | 'modalStep1Image' | 'modalStep2Image' | 'modalStep3Image';
type MediaField = { field: ImageField; label: string };

/** Image upload surfaced inline per block (the form-value field it maps to). */
const BLOCK_MEDIA: Partial<Record<InfoModalBlockType, MediaField[]>> = {
  [InfoModalBlockType.LOGO]: [{ field: 'modalLogoImage', label: 'Logo' }],
  [InfoModalBlockType.STEP_ONE]: [{ field: 'modalStep1Image', label: 'Image' }],
  [InfoModalBlockType.STEP_TWO]: [{ field: 'modalStep2Image', label: 'Image' }],
  [InfoModalBlockType.STEP_THREE]: [{ field: 'modalStep3Image', label: 'Image' }],
};

const store = useCustomizationsStore();
const media = useMediaStore();
const toast = useToastStore();
const { infoModalFormValues, builderConfigTree, builderSelectedNodeId } = storeToRefs(store);

const selectedNode = computed(() =>
  builderSelectedNodeId.value ? findNode(builderConfigTree.value, builderSelectedNodeId.value) : null,
);

const blockMedia = computed<MediaField[]>(() =>
  selectedNode.value ? (BLOCK_MEDIA[selectedNode.value.type as InfoModalBlockType] ?? []) : [],
);

function fieldValue(field: ImageField): string {
  return (infoModalFormValues.value[field] as string) || '';
}

// Inspector edit → update the node (keeps the inspector in sync) and write through to the flat
// form values the canvas + save path read. Each control's `attr` IS the form-value field key.
function onChange(attr: string, value: string | number | boolean) {
  if (!builderSelectedNodeId.value) return;
  store.updateBuilderNodeAttr(builderSelectedNodeId.value, attr, value);
  syncBlockChangeToInfoModal(attr, value, infoModalFormValues.value as Record<string, unknown>);
}

async function handleMediaUpdate(field: ImageField, images: (File | string)[]) {
  // Plain URL(s) (including removal) — write straight through.
  if (images.every(image => typeof image === 'string')) {
    (infoModalFormValues.value as Record<string, unknown>)[field] = (images[0] as string) || '';

    return;
  }

  const files = images.filter((img): img is File => img instanceof File);
  if (files.length === 0) return;

  try {
    const [uploaded] = await media.uploadMultiple(files, CLAIM_MEDIA_TYPES.branding);
    if (uploaded) (infoModalFormValues.value as Record<string, unknown>)[field] = uploaded;
  } catch (error) {
    console.error('Error uploading media', error);
    toast.addToast({ message: 'Failed to upload image. Please try again.', timeout: 5000, type: 'error' });
  }
}
</script>

<style scoped>
.media-uploader-sm :deep(.h-\[114px\].w-\[114px\]) {
  height: 80px;
  width: 80px;
}
</style>
